import React, {type ReactNode, useState, useMemo} from 'react'
import {createPortal} from 'react-dom'
import {useFloating, useInteractions, useClick, useDismiss, useRole, flip, type Placement} from '@floating-ui/react'
import clsx from 'clsx'
import styles from './Dropdown.module.scss'

interface DropdownProps {
    children: ReactNode
    className?: string | undefined
    options: {
        label: ReactNode
        onClick?: VoidFunction
        hidden?: boolean | undefined
        disabled?: boolean | undefined
    }[]
    placement?: Placement
}

const Dropdown = ({className, children, options, placement = 'bottom-start'}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        middleware: [flip()],
        strategy: 'fixed',
        onOpenChange: setIsOpen,
        placement: placement,
    })
    const click = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context)
    const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([click, dismiss, role])

    const visibleOptions = useMemo(() => options.filter((item) => !item.hidden), [options])

    const optionElements = visibleOptions.map((item, index) => (
        <div
            className={clsx(styles.menuItem, {
                [styles.disabled]: item.disabled,
            })}
            key={index}
            onClick={() => {
                if(item.disabled == true) {
                    return
                }
                item.onClick?.()
                setIsOpen(false)
            }}
            {...getItemProps()}
        >
            {item.label}
        </div>
    ))

    return (
        <>
            <button
                className={clsx(styles.menuButton, className)}
                ref={refs.setReference}
                {...getReferenceProps()}
            >
                {children}
            </button>
            {isOpen == true &&
                createPortal(<div
                    className={clsx(styles.menuItems)}
                    ref={refs.setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    {optionElements}
                </div>,
                document.body)}
        </>
    )
}

export {Dropdown, type DropdownProps}
